import { useEffect } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import pkgCountryCodes from 'countries-code'
import { countryCodes } from 'src/Libs/flow/constants'
import { getTimezonesForCountry } from 'countries-and-timezones'
import { useUserSettings } from 'src/Hooks'

interface ReturnType {
  locationData: string
  hasLocationError?: string
  timezoneOptions: Options<string>
  someDataIsFilled: boolean
  handleCountryChange: (newCountry: string) => void
  isUserSettingsLoading: boolean
  areAllFieldsEmpty: boolean
  googleAgreed?: boolean
}

export const useLocationForm = (): ReturnType => {
  const { control, setValue, trigger } = useInstallationForm()
  const { errors } = useFormState({ control, name: 'generalSettings.location' })

  // If one of these fields is given, the rest are required.
  const locationData = useWatch({
    control,
    name: [
      'generalSettings.location.street',
      'generalSettings.location.houseNumber',
      'generalSettings.location.postalCode',
      'generalSettings.location.location',
    ],
  })

  const hasLocationError = errors.generalSettings?.location?.message

  const [street, houseNumber, postalCode, location] = locationData

  useEffect(() => {
    trigger('generalSettings.location')
  }, [trigger, street, houseNumber, postalCode, location])

  const selectedCountry = useWatch({ control, name: 'generalSettings.location.country' })
  const convertedCode = pkgCountryCodes.convertAlphaCode(selectedCountry ?? countryCodes.germany)
  const timezoneOptions: Options<string> =
    getTimezonesForCountry(convertedCode)?.map(countryTimezone => ({
      value: countryTimezone.name,
      label: countryTimezone.name,
    })) ?? []

  const handleCountryChange = (newCountry: string): void => {
    // reset time zone
    const newCountryCode = pkgCountryCodes.convertAlphaCode(newCountry)
    const firstAvailableTimezone = getTimezonesForCountry(newCountryCode)?.[0]?.name ?? ''
    setValue('generalSettings.location.timeZone', firstAvailableTimezone, { shouldDirty: true })
  }

  const { userSettings, isUserSettingsLoading } = useUserSettings()

  const googleAgreed = userSettings?.googleAgreed
  const someDataIsFilled = !!(street || houseNumber || postalCode || location)
  const areAllFieldsEmpty = locationData.every(datum => !datum.trim())

  return {
    locationData: location,
    hasLocationError,
    timezoneOptions,
    someDataIsFilled,
    handleCountryChange,
    isUserSettingsLoading,
    areAllFieldsEmpty,
    googleAgreed: !!googleAgreed,
  }
}
